// resilience — the pure half of how innernet survives a bad moment.
//
// A person who is signing up, or reading the deck, or in the middle of a
// thought must never meet a framework error. When something throws, three
// things are true and the code here is the shared memory of all three:
//
//   · most trips are a moment, not a bug (a dropped request, a chunk that was
//     mid-deploy), so we put the thing back together quietly, a few times, on a
//     schedule that gets patient (BOUNDARY_BACKOFF_MS / PAGE_BACKOFF_MS);
//   · a bug that will always throw must not loop, so every retry is counted
//     against the error itself (digest) in sessionStorage and the count is the
//     cap (readAttempts / bumpAttempts);
//   · a stale deploy is a different animal — React.lazy remembers a rejected
//     chunk for the life of the page, so no remount can fix it. One reload can,
//     once a minute, and never twice in a row (chunkVerdict).
//
// Nothing in here touches React or the DOM, so all of it is unit-tested.

/** how a component-level boundary (SafeBoundary) retries: it remounts its
 *  children after each of these waits, then gives up to a manual button. */
export const BOUNDARY_BACKOFF_MS = [800, 2500, 6000] as const;

/** how a whole page (error.tsx) retries — slower, because a retry re-fetches
 *  the route from the server and the server may be the thing that is unwell. */
export const PAGE_BACKOFF_MS = [1500, 4000, 10000] as const;

/** the wait before attempt number `attempt` (0-based), or null once the
 *  schedule is spent — the signal to stop trying on our own. */
export function backoffDelay(schedule: readonly number[], attempt: number): number | null {
  if (!Number.isFinite(attempt) || attempt < 0) return schedule.length ? schedule[0] : null;
  return attempt < schedule.length ? schedule[attempt] : null;
}

/* ------------------------------------------------------------------ chunk errors */

/** what a failed dynamic import says, in every browser and bundler we ship to:
 *  webpack ("Loading chunk 12 failed"), Turbopack ("Failed to load chunk"),
 *  Chrome ("Failed to fetch dynamically imported module"), Safari
 *  ("Importing a module script failed"), Firefox ("error loading dynamically
 *  imported module"). */
export const CHUNK_ERROR_RE =
  /ChunkLoadError|Loading chunk .* failed|Failed to load chunk|dynamically imported module|Importing a module script failed|error loading dynamically imported/i;

/** true when `err` is a failed code download rather than a bug in our code. */
export function isChunkError(err: unknown, depth = 0): boolean {
  if (err == null || depth > 3) return false;
  if (typeof err === "string") return CHUNK_ERROR_RE.test(err);
  if (typeof err !== "object") return false;
  const e = err as { name?: unknown; message?: unknown; cause?: unknown; reason?: unknown };
  if (typeof e.name === "string" && CHUNK_ERROR_RE.test(e.name)) return true;
  if (typeof e.message === "string" && CHUNK_ERROR_RE.test(e.message)) return true;
  return isChunkError(e.cause, depth + 1) || isChunkError(e.reason, depth + 1);
}

/** The signal a boundary sends to ChunkRecovery (mounted once in the layout)
 *  when it has watched the same code fail to download twice. */
export const CHUNK_EVENT = "innernet:chunk-error";

export function signalChunkError(err: unknown): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(CHUNK_EVENT, { detail: err }));
  } catch {
    /* a signal must never throw on top of an error */
  }
}

/* -------------------------------------------------------------- retrying an import */

const pause = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Run a dynamic import, and if the DOWNLOAD fails (a dropped connection, a CDN
 * hiccup) try it again a moment later. A module that downloaded and then threw
 * while running is a bug, not a blip, so it is rethrown at once.
 *
 *   dynamic(() => retryImport(() => import("./Panel")))
 *
 * After the last attempt `onGiveUp` runs (so the caller can note the loader is
 * dead and needs a fresh one — see rearmable in SafeBoundary) and the final
 * error is thrown for a boundary to catch.
 */
export async function retryImport<T>(
  load: () => Promise<T>,
  opts: { delays?: readonly number[]; onGiveUp?: (err: unknown) => void; sleep?: (ms: number) => Promise<void> } = {},
): Promise<T> {
  const delays = opts.delays ?? [400, 1500];
  const sleep = opts.sleep ?? pause;
  let last: unknown;
  for (let i = 0; i <= delays.length; i++) {
    try {
      return await load();
    } catch (err) {
      last = err;
      if (!isChunkError(err)) throw err;
      if (i < delays.length) await sleep(delays[i]);
    }
  }
  try {
    opts.onGiveUp?.(last);
  } catch {
    /* ignore */
  }
  throw last;
}

/* -------------------------------------------------------------- per-error attempts */

/** the slice of Storage we use, so tests can hand in a Map. */
export interface AttemptStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** an error's attempts are forgotten after this long without one — a bug that
 *  returns tomorrow starts a fresh schedule. */
export const ATTEMPT_TTL_MS = 120_000;

/** the key an error's attempts are counted under: where it happened + its
 *  digest (a server error) or the start of its message (a client one). */
export function errorKey(where: string, error: { digest?: string; message?: string } | null | undefined): string {
  const id = error?.digest || (error?.message ?? "unknown").slice(0, 80);
  return `innernet:retry:${where}:${id}`;
}

/** how many automatic retries this error has already had (0 when unseen, old,
 *  or when storage is unavailable — then the in-memory fallback below caps it). */
export function readAttempts(store: AttemptStore | null, key: string, now: number): number {
  const mem = memory.get(key);
  let stored = 0;
  try {
    const raw = store?.getItem(key);
    if (raw) {
      const { n, t } = JSON.parse(raw) as { n?: number; t?: number };
      if (typeof n === "number" && typeof t === "number" && now - t <= ATTEMPT_TTL_MS) stored = n;
    }
  } catch {
    /* corrupt or blocked: fall through to memory */
  }
  const fromMemory = mem && now - mem.t <= ATTEMPT_TTL_MS ? mem.n : 0;
  return Math.max(stored, fromMemory);
}

/** record one more automatic retry and return the new count. */
export function bumpAttempts(store: AttemptStore | null, key: string, now: number): number {
  const n = readAttempts(store, key, now) + 1;
  memory.set(key, { n, t: now });
  try {
    store?.setItem(key, JSON.stringify({ n, t: now }));
  } catch {
    /* private mode: the memory copy still caps this page's life */
  }
  return n;
}

/** a failure that keeps changing its digest (an error that carries a timestamp,
 *  a different server instance each time) would walk around the per-error cap,
 *  so everything under one `where` also shares a total: the schedule and a little
 *  more. */
export const WHERE_SLACK = 2;

/** the key that counts every automatic retry for a place, whatever the error. */
export function whereKey(where: string): string {
  return `innernet:retry:${where}:*`;
}

/** the wait before the next automatic retry of this error at this place, or null
 *  once either its own count or the place's total is spent. */
export function nextRetryDelay(
  store: AttemptStore | null,
  where: string,
  error: { digest?: string; message?: string } | null | undefined,
  schedule: readonly number[],
  now: number,
): number | null {
  if (readAttempts(store, whereKey(where), now) >= schedule.length + WHERE_SLACK) return null;
  return backoffDelay(schedule, readAttempts(store, errorKey(where, error), now));
}

/** record that an automatic retry was just fired, against the error and the place. */
export function recordRetry(
  store: AttemptStore | null,
  where: string,
  error: { digest?: string; message?: string } | null | undefined,
  now: number,
): void {
  bumpAttempts(store, errorKey(where, error), now);
  bumpAttempts(store, whereKey(where), now);
}

// a second copy that survives a blocked sessionStorage for the life of the page
const memory = new Map<string, { n: number; t: number }>();

/** test hook: forget the in-memory copy. */
export function _resetAttemptMemory(): void {
  memory.clear();
}

/* ------------------------------------------------------------------ stale deploys */

export const CHUNK_RELOAD_KEY = "innernet:chunk-reload";
export const CHUNK_WINDOW_MS = 60_000;

export type ChunkVerdict = "reload" | "give-up";

/**
 * A chunk failed to download. Reloading fetches the new build, and is the only
 * thing that can fix it, but a reload that meets the same failure must not run
 * again. So: reload once, and stamp the time; a second failure inside the
 * window gives up to a manual button. If we cannot stamp (storage blocked) we
 * cannot promise there is no loop, so we give up rather than risk one.
 */
export function chunkVerdict(store: AttemptStore | null, now: number): ChunkVerdict {
  if (!store) return "give-up";
  try {
    const last = Number(store.getItem(CHUNK_RELOAD_KEY));
    if (Number.isFinite(last) && last > 0 && now - last <= CHUNK_WINDOW_MS) return "give-up";
    store.setItem(CHUNK_RELOAD_KEY, String(now));
    return "reload";
  } catch {
    return "give-up";
  }
}

/** true when this tab reloaded for a chunk failure within the last window. */
export function justReloadedForChunk(store: AttemptStore | null, now: number): boolean {
  try {
    const last = Number(store?.getItem(CHUNK_RELOAD_KEY));
    return Number.isFinite(last) && last > 0 && now - last <= CHUNK_WINDOW_MS;
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------- shapes from the wire */

/**
 * A poll answered 200 with something that is not a list. Trusting it threw
 * inside a state updater, which is a render crash of the whole shell. Returns
 * the well-formed rows, or null when the payload is not a list at all (the
 * caller keeps what it has). A row without an id and a name is dropped, never
 * guessed at.
 */
export function wellFormedRows<T extends { id: string; name: string }>(payload: unknown): T[] | null {
  if (!Array.isArray(payload)) return null;
  return payload.filter(
    (r): r is T => !!r && typeof r === "object" && typeof (r as T).id === "string" && typeof (r as T).name === "string",
  );
}
